import {DEFAULT_COMBINE_GRAPHS_MODE, DropdownInput, FormFragment} from 'apstra-ui-common';
import {map} from 'lodash';
import {FC} from 'react';
import {Grid} from 'semantic-ui-react';

import checkForPatterns, {ProbePattern} from '../checkForPatterns';
import {DEFAULT_STAGE_DATA_SOURCE, DEFAULT_STAGE_TIME_SERIES_AGGREGATION,
  DEFAULT_STAGE_TIME_SERIES_DURATION, STAGE_DATA_SOURCE} from '../consts';
import {getDefaultAggregationType} from '../stageUtils';
import {Probe, ProbeProcessor, ProbeStage, RenderingStrategy} from '../types';
import {useStageOptionsSchema} from '../hooks/useStageOptionsSchema';

type StageDataOptionsProps = {
  filters: any;
  isSpotlightMode: boolean;
  patternDescription: ProbePattern | null;
  probe: Probe;
  processor: ProbeProcessor;
  renderingStrategy: RenderingStrategy;
  stage: ProbeStage;
  valueColumnName: string;
  updateFilters: (filters: any, options?: any) => void;
}

export const StageDataOptions: FC<StageDataOptionsProps> = ({
  filters,
  isSpotlightMode,
  patternDescription,
  probe,
  processor,
  renderingStrategy,
  stage,
  updateFilters,
  valueColumnName,
}) => {
  const optionsSchema = useStageOptionsSchema({
    DataSourceComponent: DropdownInput,
    aggregationType: filters.aggregationType,
    canShowTelemetryServiceWarningsDataSource: true,
    dataSource: filters.dataSource,
    descriptionPosition: 'tooltip',
    isProbeOperational: probe.state === 'operational',
    isSpotlightMode,
    patternDescription,
    processor,
    renderingStrategy,
    shouldIncludeLabels: true,
    showContext: filters.showContextInfo,
    stage,
    valueColumnName,
  });

  const onOptionChange = (name: string, value: any) => {
    if (name === 'aggregation_period') {
      updateFilters({...filters, timeSeriesAggregation: value}, {resetActivePage: false});
    } else if (name === 'aggregation_type') {
      updateFilters(
        {
          ...filters,
          aggregationType: value,
          timeSeriesAggregation: value === 'none' ?
              0 : filters.timeSeriesAggregation || DEFAULT_STAGE_TIME_SERIES_AGGREGATION
        },
        {resetActivePage: false}
      );
    } else if (name === 'anomalous_only') {
      updateFilters({...filters, anomalousOnly: value});
    } else if (name === 'combine_graphs') {
      updateFilters({...filters, combineGraphs: value});
    } else if (name === 'data_source') {
      const patternDescription = checkForPatterns({probe, stageName: stage.name, dataSource: value});
      const changes = {dataSource: value} as any;
      if (value === STAGE_DATA_SOURCE.telemetry_service_warnings ||
          filters.dataSource === STAGE_DATA_SOURCE.telemetry_service_warnings) {
        if (filters.filter) changes.filter = {};
      }
      if (value === STAGE_DATA_SOURCE.time_series) {
        if (!filters.timeSeriesDuration) {
          changes.timeSeriesDuration = DEFAULT_STAGE_TIME_SERIES_DURATION;
        }
        if (filters.showContextInfo && !patternDescription?.renderingStrategy?.alwaysUseContext) {
          changes.showContextInfo = false;
        }
      } else if (value === STAGE_DATA_SOURCE.telemetry_service_warnings) {
        if (filters.spotlightMode) {
          changes.spotlightMode = false;
        }
      } else if (patternDescription) {
        changes.showContextInfo = !patternDescription.renderingStrategy?.hiddenContextByDefault;
      }
      updateFilters({...filters, ...changes});
    } else if (name === 'show_context') {
      if (!value && patternDescription?.renderingStrategy?.alwaysUseContext) return;
      updateFilters({...filters, showContextInfo: value});
    } else if (name === 'time_series_duration') {
      updateFilters({...filters, timeSeriesDuration: value}, {resetActivePage: false});
    } else if (name === 'value_column_name') {
      updateFilters({
        ...filters,
        valueColumnName: value,
        aggregationType: getDefaultAggregationType(stage, value, patternDescription)
      });
    }
  };

  if (isSpotlightMode) return null;

  return (
    <>
      {map(optionsSchema, (schemaLine, index) => (
        <Grid.Row key={index}>
          <Grid.Column width={16}>
            <div className='stage-options-form'>
              <FormFragment
                onChange={onOptionChange}
                schema={schemaLine}
                values={{
                  aggregation_period: filters.timeSeriesAggregation,
                  aggregation_type: filters.aggregationType,
                  anomalous_only: filters.anomalousOnly,
                  combine_graphs: filters.combineGraphs || DEFAULT_COMBINE_GRAPHS_MODE,
                  data_source: filters.dataSource || DEFAULT_STAGE_DATA_SOURCE,
                  show_context: filters.showContextInfo,
                  time_series_duration: filters.timeSeriesDuration || DEFAULT_STAGE_TIME_SERIES_DURATION,
                  value_column_name: valueColumnName,
                }}
              />
            </div>
          </Grid.Column>
        </Grid.Row>
      ))}
    </>
  );
};
